import { Descriptions } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { deletePopup, getPopup } from "../../../api/popup.api";
import Button from "../../../components/Button";
import ErrorResponse from "../../../components/ErrorResponse";
import Header from "../../../components/layout/Header";
import Loader from "../../../components/Loader";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import { IGetPopup } from "../../../models/popup.model";
import { RootState } from "../../../store/rootReducer";
import {getWebUrl} from "../../../lib/util";

interface IMatch {
  campusCode: string;
  popupCode: string;
}

const PopupRead = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const campusCode = match.params.campusCode;
  const popupCode = Number(match.params.popupCode);
  const history = useHistory();
  const location = useLocation();

  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetPopup>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  useEffect(() => {
    if (campusCode && popupCode) {
      execute(getPopup(campusCode, popupCode));
    }
  }, [campusCode, popupCode]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.delete'));
      history.push(`/homepage/popup${location.search}`);
    }
  }, [submitRes, submitIsLoading]);

  const getCampusName = (campusCode: string) => {
    const campus = campusData?.find((item: any) => item.campusCode === campusCode);
    return campus?.campusKorName || '';
  }

  const onDelete = () => {
    if (!campusCode || !popupCode) return;

    submitExecute(deletePopup(campusCode, popupCode));
  }

  function virtualPrevPopup() {
    window.open(`${getWebUrl()}/canb/virtual/preview/popup/${popupCode}`, '_blank');
  }

  return (
    <div className="content-wrapper">
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <Header
        iconFile="icon-homepage-big.svg"
        parentMenu={t('common.menu.homepage')}
        currentMenu={t('homepage.popup.text1')}
      />

      <div className="main">
        <Descriptions
          bordered
          column={4}
          size="small"
        >
          <Descriptions.Item label={t('common.column.title')} span={4}>
            {res?.data?.title}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.writer')}>
            {res?.data?.regMemKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column8')}>
            {moment(res?.data?.firstRegDttm || '').format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.education.column11')}>
            {getCampusName(res?.data?.campusCode || '')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.popup.text2')}>
            {res?.data?.showOrder}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.popup.text3')}>
            {res?.data?.positionLeft}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.popup.text4')}>
            {res?.data?.positionTop}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.popup.text5')}>
            {moment(res?.data?.startDate || '').format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.popup.text6')}>
            {moment(res?.data?.endDate || '').format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.contents')} span={4}>
            <div dangerouslySetInnerHTML={{ __html: res?.data?.content || '' }} />
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.popup.text7')} span={4}>
            {res?.data?.openYn}
          </Descriptions.Item>
        </Descriptions>

        <div className="row mt-4 mb-2">
          <div className="col-6">
            <Button
              className="btn btn-sm btn-primary btn-w100p mr-1"
              text={t('common.button.list')}
              onClick={() => history.push(`/homepage/popup${location.search}`)}
            />
          </div>
          <div className="col-6 text-right">
            <Button
              className="btn btn-sm btn-secondary btn-w100p mr-1"
              text={t('homepage.popup.1')}
              onClick={() => virtualPrevPopup()}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className="btn btn-sm btn-secondary btn-w100p mr-1"
              text={t('common.button.delete')}
              onClick={() => setConfirmDeleteVisible(true)}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className="btn btn-sm btn-primary btn-w100p"
              text={t('common.button.edit')}
              onClick={() => history.push(`/homepage/popup/${campusCode}/${popupCode}/edit${location.search}`)}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>

      {confirmDeleteVisible && (
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </div>
  );
}

export default PopupRead;
